import * as React from 'react';
import { Options } from 'ui';
import { BreadcrumbContainer } from 'ui/common/select/breadcrumb-container';
import { useProcessedOptions, useShowLongDescriptionState } from 'ui/common/select/options-container-hooks';
import { OptionsResolver } from 'ui/common/select/options-resolver';
import { achievedMinLength } from 'ui/common/select/select-base-component';
import { SelectOptionsExpander } from 'ui/common/select/select-options-expander';
import { OptionsType, SelectCommonProps, SelectItem, SelectType } from 'ui/common/select/types';
import { Switch } from 'ui/common/switch/switch';
import isNumber from 'lodash/isnumber';

interface OptionsContainerProps
  extends SelectCommonProps, Options<number | string> {
  selectType: SelectType;
  readOnly?: boolean;
  valueItems?: SelectItem[];
  onChange: (data: SelectItem | SelectItem[]) => void;

  // hierarchy props
  minLevel?: number;
  levelsNotAllowed?: boolean;
  leafNodeOnly?: boolean;
  descriptionAllLevels?: boolean;
  showBackgroundColour?: boolean;
  displayCategories?: boolean;

  inputValue: string | undefined;
  onSelectOption: (item: SelectItem) => void;
  onCloseOptions: (moveFocus?: boolean) => void;
  categoryId: number | undefined;
  setCategoryId: (categoryId: number) => void;
  isOpen: boolean;
  isClosing: boolean;
  includeDisabledCheckbox?: JSX.Element;
}

// eslint:disable-next-line: max-func-body-length
export function OptionsContainer({
  fetching, loadOptionsError, valueItems, readOnly, options, alwaysOpen,
  longDescription, selectType, loadOptions, minLevel, leafNodeOnly, descriptionAllLevels, optionsDisplaySetting,
  showIcons, hideAlphabeticIndex, searchRequired, textSearchRequired, textSearchRequiredExceedsMaxResult, levelsNotAllowed,
  showBackgroundColour, displaySelectAll, textSelectAll, textClearAll, extraFooterButton, showIndexWhenNoOfPagesIsAbove,
  displayCategories, categoryOptions, onLoadOptions, onLoadCategoryOptions, minLength, inputValue, onSelectOption, onChange, onCloseOptions,
  setCategoryId, categoryId, isOpen, isClosing, textOptionsLoadError, textOptionsNotFound, textOptionsDone, textPageOf,
  loadCategoryOptions, initiallyShowLongDescription, includeDisabledCheckbox, includeDisabled
}: OptionsContainerProps) {

  const {
    showLongDescription,
    onShowLongDescription
  } = useShowLongDescriptionState(longDescription && initiallyShowLongDescription);

  const {
    breadcrumbStack, returnedOptions, allLevelsDescriptionPrefix, returnedCategoryOptions
  } = useProcessedOptions(
    options, loadOptions, categoryOptions,
    loadCategoryOptions, descriptionAllLevels,
    displayCategories, categoryId
  );

  const closingAnimation = isClosing ? 'animate-out' : '';
  const achievedMinLengthCondition = achievedMinLength(inputValue, minLength);
  const notEnoughInputWhenSearchRequired = searchRequired && !achievedMinLengthCondition;
  const hasNewInputValue =
    !notEnoughInputWhenSearchRequired &&
    loadOptions?.query &&
    inputValue?.trim() !== loadOptions.query.trim() &&
    achievedMinLengthCondition;
  const loadingOptions = fetching || hasNewInputValue;
  const doNotRenderExpander = !!alwaysOpen;

  const categoryHasChildren = (id: number): boolean => {
    const findItem = (array = []) => {
      let result: SelectItem;
      array.some(o => result = o.value === id ? o : findItem(o.descendants || []));
      return result;
    };
    const categoryItem = findItem(categoryOptions);
    return categoryOptions?.length && categoryItem?.descendants?.length > 0;
  };

  const categoryNotSet = !isNumber(categoryId);
  const notASearchMode = !loadOptions?.query?.length;

  const showCategoryOptions = displayCategories && notASearchMode &&
    (categoryNotSet || categoryHasChildren(categoryId));

  const longDescriptionExists = showCategoryOptions ?
    returnedCategoryOptions?.some((i) => !!i.longDescription)
    :
    returnedOptions?.some((i) => !!i.longDescription);
  const switchButton = longDescription && longDescriptionExists
    && <Switch checked={showLongDescription} onChange={onShowLongDescription} />;
  const switchLongDescriptionAvailable = longDescription && longDescriptionExists;

  const breadcrumb = <BreadcrumbContainer
    breadcrumbStack={breadcrumbStack}
    categoryId={categoryId}
    setCategoryId={setCategoryId}
    onLoadOptions={onLoadOptions}
    levelsNotAllowed={levelsNotAllowed}
    includeDisabled={includeDisabled}
  />;

  return !readOnly && isOpen && (
    <div className={closingAnimation}>
      <SelectOptionsExpander
        loadingOptions={loadingOptions}
        loadOptionsError={loadOptionsError}
        onClose={onCloseOptions}
        texts={{
          loadError: textOptionsLoadError,
          notFound: textOptionsNotFound,
          done: textOptionsDone,
          searchRequired: textSearchRequired
        }}
        doNotRenderExpander={doNotRenderExpander}
        switchButton={switchButton}
        switchLongDescriptionAvailable={switchLongDescriptionAvailable}
        notEnoughInputWhenSearchRequired={notEnoughInputWhenSearchRequired}
        extraFooterButton={extraFooterButton}
        optionsAvailable={!!options?.length}
        includeDisabledCheckbox={includeDisabledCheckbox}
      >
        {(!!options?.length || !!categoryOptions?.length) && (
          <OptionsResolver
            valueItems={valueItems}
            options={options}
            optionsDisplaySetting={
              hideAlphabeticIndex ? { type: OptionsType.flat } : optionsDisplaySetting
            }
            notFoundText={textOptionsNotFound}
            showIcons={showIcons}
            setCategoryId={setCategoryId}
            onSelectOption={onSelectOption}
            onChange={onChange}
            selectType={selectType}
            onLoadOptions={onLoadOptions}
            onLoadCategoryOptions={onLoadCategoryOptions}
            loadOptions={loadOptions}
            minLevel={minLevel}
            levelsNotAllowed={levelsNotAllowed}
            leafNodeOnly={leafNodeOnly}
            descriptionAllLevels={descriptionAllLevels}
            showLongDescription={showLongDescription && longDescriptionExists}
            showBackgroundColour={showBackgroundColour}

            switchButton={!!alwaysOpen && switchButton}
            includeDisabledCheckbox={includeDisabledCheckbox}
            includeDisabled={includeDisabled}

            displaySelectAll={displaySelectAll}
            textSelectAll={textSelectAll}
            textClearAll={textClearAll}

            allLevelsDescriptionPrefix={allLevelsDescriptionPrefix}
            breadcrumbStack={breadcrumbStack}
            breadcrumb={breadcrumb}
            returnedOptions={returnedOptions}
            returnedCategoryOptions={returnedCategoryOptions}

            searchRequired={searchRequired}
            textSearchRequired={textSearchRequired}
            textSearchRequiredExceedsMaxResult={textSearchRequiredExceedsMaxResult}
            textPageOf={textPageOf}
            notEnoughInputWhenSearchRequired={notEnoughInputWhenSearchRequired}
            showIndexWhenNoOfPagesIsAbove={showIndexWhenNoOfPagesIsAbove}
            categoryOptions={categoryOptions}
            showCategoryOptions={showCategoryOptions}
          />
        )}
      </SelectOptionsExpander>
    </div>
  );
}
